import React, { useEffect } from 'react';

import Input from '@/components/General/inputs/Input';
import ToggleRow from '@/components/General/inputs/ToggleRow';
import CustomField from '@/components/CustomFields/CustomField';
import { useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import InputSection from '@/components/General/inputs/InputSection';
import { Query } from '@/utils/react-query/Queries';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import { supabase } from '@/data/client';
import { saveSmallGroup } from '@/data/smallGroups';
import Toast from 'react-native-toast-message';
import { useEditScreenStore } from '@/data/store/useEditScreenStore';
import { redirectToDefault } from '@/utils/constants/linking';
import { useLocalSearchParams, useRouter } from 'expo-router';
import ModalWrapper from '@/components/General/ModalWrapper';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { showAlert } from '@/utils/showAlert';
import useBibleStudies from '@/utils/hooks/useBibleStudies';
import { useMemo } from 'react';

const EMPTY_DRAFT = {};

export default function AddSmallGroupModal() {
    const { id, fromScreen, fromStoreKey, typeID } = useLocalSearchParams();
    const storeKey = id || "new"

    const queryClient = useQueryClient()
    const removeEditScreenData = useEditScreenStore(s => s.removeEditScreenData)
    const setEditScreenData = useEditScreenStore(s => s.setEditScreenData)
    const router = useRouter();

    const smallGroupDetails = useEditScreenStore(s => s.AddSmallGroupModal?.[storeKey]) || EMPTY_DRAFT

    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: existingItem } = Query.smallGroups.byId({id: id, options: { enabled: !!id }});
    const smallGroupTypesQuery = Query.smallGroupTypes.all({});
    const { data: smallGroupTypes } = smallGroupTypesQuery;
    const { enabled: bibleStudiesEnabled } = useBibleStudies();

    // Flag-on: Bible Study types live in the Bible Studies module — exclude
    // them from this ordinary type picker so a legacy flow can't create one.
    const ordinaryTypesQueryResult = useMemo(() => bibleStudiesEnabled ? ({
        ...smallGroupTypesQuery,
        data: (smallGroupTypes || []).filter(t => t.purpose !== 'BIBLE_STUDY'),
    }) : undefined, [bibleStudiesEnabled, smallGroupTypes, smallGroupTypesQuery]);
    
    const defaultSmallGroupType = smallGroupTypes?.find(t => t.name === 'Small Group');
    const selectedType = smallGroupTypes?.find(t => t.id === typeID) || smallGroupTypes?.find(t => t.id === smallGroupDetails?.smallGroupTypeID) || defaultSmallGroupType;
    const roleID = selectedType?.roleID;
    const typeName = selectedType?.name || 'Small Group';
    const { data: smallGroupTypeSections } = Query.customFieldSections.all({module: "SmallGroupType", options: { enabled: !!selectedType?.id }});

    const rolePeopleQuery = useInfiniteQuery({
        queryKey: ['peopleByRole', roleID],
        queryFn: async ({pageParam: _pageParam = 0}) => {
            const { data, error } = await supabase.from('RolePeople')
                .select('Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)')
                .eq('roleID', roleID);
            if (error) throw error;
            const people = data?.map(r => r.Person).filter(Boolean) || [];
            people.sort((a, b) => (a.lastName || '').localeCompare(b.lastName || '') || (a.firstName || '').localeCompare(b.firstName || ''));
            return { nextPage: undefined, data: people };
        },
        getNextPageParam: () => undefined,
        enabled: !!roleID
    });
    
    const saveSmallGroupMutation = useMutation({
        mutationFn: saveSmallGroup,
        onSuccess: (data) => {
            removeEditScreenData("AddSmallGroupModal", storeKey)
            queryClient.invalidateQueries({queryKey: ['smallGroups']});
            queryClient.invalidateQueries({queryKey: ['dashboardData']});
            queryClient.invalidateQueries({queryKey: ['people']});
            Toast.delayShow({type: 'success', text2: `New ${typeName} has been ${id ? 'updated' : 'created'} successfully!`})

            if(fromScreen){
                useEditScreenStore.getState().dataChangeFunction?.(data)
                router.dismissTo({pathname:fromScreen, params: fromStoreKey && fromStoreKey != "new" ? {id: fromStoreKey} : undefined});
            }
            else{
                if(id){
                    redirectToDefault(router, "AddSmallGroupModal")
                }
                else{
                    router.replace({pathname: "/smallgroups/sessions/create", params: {smallGroupID: data.id}})
                }
            }
        }
    })

    /*** Effects ***/
    useEffect(() => {
        // Re-run when loggedInUser resolves — on a cold session it is still
        // undefined at mount, which would leave Leader unset forever and
        // validation ("Please select a Leader") would silently block save.
        if(!existingItem && loggedInUser && !smallGroupDetails?.Leader){
            setSmallGroupDetails({ ...smallGroupDetails, leaderID: loggedInUser.id, Leader: loggedInUser, Students: smallGroupDetails?.Students || [], smallGroupTypeID: smallGroupDetails?.smallGroupTypeID || selectedType?.id, SmallGroupType: smallGroupDetails?.SmallGroupType || selectedType })
        }
    }, [loggedInUser, selectedType, existingItem])

    // Flag-on: a bible-study typeID (or an edit link for a study) belongs in
    // the Bible Studies module — re-route rather than render the legacy form.
    useEffect(() => {
        if (!bibleStudiesEnabled) return;
        const targetPurpose = selectedType?.purpose || existingItem?.SmallGroupType?.purpose;
        if (targetPurpose === 'BIBLE_STUDY') {
            router.replace({ pathname: '/biblestudies/create', params: { id: id || '' } });
        }
    }, [bibleStudiesEnabled, selectedType?.purpose, existingItem?.SmallGroupType?.purpose, id, router]);

    useEffect(() => {
        if(!existingItem && selectedType?.id && !smallGroupDetails?.smallGroupTypeID){
            setSmallGroupDetails({...smallGroupDetails, smallGroupTypeID: selectedType.id, SmallGroupType: selectedType})
        }
    }, [selectedType, existingItem])

    useEffect(() => {
        if(existingItem){
            setSmallGroupDetails({
                ...existingItem,
                lessonTime: existingItem?.lessonTime ? new Date('2025-01-01 ' + existingItem?.lessonTime) : undefined,
                Students: existingItem?.Students?.map(s => s.Person),
                originalValues: existingItem,
            })
        }
    }, [existingItem]);
    
    const setSmallGroupDetails = (details) => {
        setEditScreenData("AddSmallGroupModal", storeKey, details)
    }

    return (
        <ModalWrapper 
            loading={saveSmallGroupMutation.isPending} 
            onConfirm={() => {
                const smallGroupToSubmit = {...smallGroupDetails}

                // if(!smallGroupToSubmit.smallGroupTypeID){
                //     Toast.show({type: 'error', text2: 'Please select a Small Group Type'})
                //     return;
                // }

                if(!smallGroupToSubmit.Leader){
                    Toast.show({type: 'error', text2: 'Please select a Leader'})
                    return;
                }

                if(!smallGroupToSubmit.Students?.length){
                    Toast.show({type: 'error', text2: 'Please select at least one Member'})
                    return;
                }

                const missingRequiredFields = (selectedType?.CustomFields || []).filter(f => f.displayOn !== 'session' && f.required && !f.isDeleted);
                const missingField = missingRequiredFields.find(f => {
                    const val = smallGroupToSubmit.customFieldValues?.[f.id];
                    return val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0);
                });
                if(missingField){
                    Toast.show({type: 'error', text2: `Please fill out the required field: ${missingField.name}`})
                    return;
                }

                if(!smallGroupToSubmit.name){
                    smallGroupToSubmit.name = smallGroupToSubmit.Students?.map(s => s.firstName).join(', ') 
                }

                const { originalValues, ...cleanSubmit } = smallGroupToSubmit;
                const submit = () => saveSmallGroupMutation.mutate({...cleanSubmit, originalValues});

                const actorIsLeader = loggedInUser?.id === smallGroupToSubmit.leaderID || loggedInUser?.id === smallGroupToSubmit.coLeaderID;
                const originalMemberIDs = (originalValues?.Students || []).map(s => s.Person?.id);
                const addedMembers = id ? (smallGroupToSubmit.Students || []).filter(s => !originalMemberIDs.includes(s.id)) : (smallGroupToSubmit.Students || []);

                if(smallGroupToSubmit.notifyLeaderOnMemberAdd && !actorIsLeader && addedMembers.length > 0){
                    const personName = p => `${p?.firstName || ''} ${p?.lastName || ''}`.trim();
                    const leaderName = personName(smallGroupToSubmit.Leader);
                    const coLeaderName = personName(smallGroupToSubmit.CoLeader);
                    const memberNames = addedMembers.map(personName).join(', ');
                    showAlert({
                        title: 'Notify Leader?',
                        subtitle: `${leaderName}${coLeaderName ? ' and ' + coLeaderName : ''} will be notified that ${memberNames} will be added to their group`,
                        confirmText: 'Save',
                        cancelText: 'Cancel',
                        onConfirm: submit,
                    });
                    return;
                }

                submit();
            }} 
            title={id ? `Update ${typeName}` : `New ${typeName}`}
        >
            <KeyboardAwareScrollView bottomOffset={100} keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
                <InputSection title="Basic Info">
                    {!typeID && <SheetSelector type="smallGroupTypes" queryResult={ordinaryTypesQueryResult} title="Small Group Type" helpText="Select the type of Small Group this is." addScreenName={loggedInUser?.can?.("Create Small Group Types") ? "smallgroups/types/create" : null} fromScreen={`smallgroups/create`} fromStoreKey={storeKey} value={smallGroupDetails.SmallGroupType} onChange={(selected_item) => {
                        setSmallGroupDetails({
                            smallGroupTypeID: selected_item?.id,
                            SmallGroupType: selected_item
                        })
                    }}/>}

                    <SheetSelector
                        required
                        type="people"
                        queryResult={roleID ? rolePeopleQuery : undefined}
                        title="Leader"
                        helpText="Select the leader who will be leading this Small Group."
                        fromScreen={`smallgroups/create`}
                        fromStoreKey={storeKey}
                        value={smallGroupDetails?.Leader}
                        onChange={(selected_item) => {
                            setSmallGroupDetails({
                                leaderID: selected_item?.id,
                                Leader: selected_item
                            })
                        }}
                    />

                    <SheetSelector
                        type="people"
                        queryResult={roleID ? rolePeopleQuery : undefined}
                        title="Co-Leader"
                        helpText="Optionally select a co-leader who shares responsibility for this group."
                        fromScreen={`smallgroups/create`}
                        fromStoreKey={storeKey}
                        value={smallGroupDetails?.CoLeader}
                        onChange={(selected_item) => {
                            setSmallGroupDetails({
                                coLeaderID: selected_item?.id,
                                CoLeader: selected_item
                            })
                        }}
                    />

                    <SheetSelector required multiple type="people" title="Members" helpText="Select the members who will be regularly attending this Small Group. You will be able to alter this list for each session." addScreenName={loggedInUser?.can?.("Create People") ? "people/create" : null} fromScreen={`smallgroups/create`} fromStoreKey={storeKey} value={smallGroupDetails.Students || []} onChange={(selected_items) => {
                        setSmallGroupDetails({
                            Students: selected_items
                        })
                    }}/>

                    <Input
                        title="Small Group Name (optional)"
                        maxLength={120}
                        value={smallGroupDetails.name}
                        onUpdate={(new_value) => {
                            setSmallGroupDetails({
                                name: new_value
                            })
                        }}
                    />

                    <Input
                        title="Description (optional)"
                        helpText="A short blurb describing this group, shown publicly on signup pages."
                        multiline
                        maxLength={2000}
                        value={smallGroupDetails.description}
                        onUpdate={(new_value) => {
                            setSmallGroupDetails({
                                description: new_value
                            })
                        }}
                    />

                    <ToggleRow
                        testID="notify-leader-toggle"
                        label="Notify the leader when members are added"
                        description="Sends a push notification and email to the leader and co-leader when someone else adds members to this group."
                        value={!!smallGroupDetails.notifyLeaderOnMemberAdd}
                        onValueChange={(v) => setSmallGroupDetails({ notifyLeaderOnMemberAdd: v })}
                    />
                </InputSection>

                {(smallGroupTypeSections || []).map(section => {
                    const sectionFields = (selectedType?.CustomFields || []).filter(f => f.sectionID === section.id && f.displayOn !== 'session');
                    if (sectionFields.length === 0) return null;
                    return (
                        <InputSection key={section.id} title={section.name}>
                            {sectionFields.map(field => (
                                <CustomField
                                    key={field.id}
                                    fieldType={field.type}
                                    required={field.required}
                                    onUpdate={(val) => {
                                        const oldVals = smallGroupDetails.customFieldValues || {};
                                        setSmallGroupDetails({ customFieldValues: { ...oldVals, [field.id]: val } });
                                    }}
                                    options={{
                                        title: field.name,
                                        value: smallGroupDetails.customFieldValues?.[field.id],
                                        options: field.options,
                                    }}
                                />
                            ))}
                        </InputSection>
                    );
                })}

            </KeyboardAwareScrollView>
        </ModalWrapper> 
    );
}